import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "rowCheckbox", "selectAll", "printButton", "printButtonLabel", "printTooltip", "modal", "columnCheckbox", "table", "printNotes"]
  static values = { columnConfig: Array, pdfUrl: String, skipColumnSelection: Boolean }

  connect() {
    this.updatePrintButton()
    document.addEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  get boundOnSubmitEnd() {
    if (!this._boundOnSubmitEnd) {
      this._boundOnSubmitEnd = (event) => {
        if (event.detail?.success) {
          const form = event.detail?.formSubmission?.formElement ?? event.target
          if (form?.dataset?.resetSelectionOnSubmit === "true") {
            setTimeout(() => this.reset(), 0)
          }
        }
      }
    }
    return this._boundOnSubmitEnd
  }

  toggleRow(event) {
    event.stopPropagation()
    const row = event.target.closest?.("[data-print-selection-target='row']")
    const checkbox = row?.querySelector("[data-print-selection-target='rowCheckbox']")
    if (!row || !checkbox) return

    const rowIndex = this.rowTargets.indexOf(row)
    if (rowIndex === -1) return

    if (event.shiftKey && this.lastClickedRowIndex != null) {
      event.preventDefault()
      const start = Math.min(this.lastClickedRowIndex, rowIndex)
      const end = Math.max(this.lastClickedRowIndex, rowIndex)
      const targetState = true // shift-click extends selection: check all in range
      // Defer so our updates run after any native checkbox handling
      setTimeout(() => {
        for (let i = start; i <= end; i++) {
          const cb = this.rowCheckboxTargets[i]
          if (cb) {
            cb.checked = targetState
            cb.dispatchEvent(new Event("change", { bubbles: true }))
          }
        }
        this.updateSelectAllState()
        this.updatePrintButton()
      }, 0)
      this.lastClickedRowIndex = rowIndex
      return
    }
    this.lastClickedRowIndex = rowIndex

    this.updateSelectAllState()
    this.updatePrintButton()
  }

  toggleAll() {
    if (!this.hasSelectAllTarget) return

    const allChecked = this.selectAllTarget.checked
    this.rowCheckboxTargets.forEach((cb) => {
      cb.checked = allChecked
    })
    this.updateSelectAllState()
    this.updatePrintButton()
  }

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const checkboxes = this.rowCheckboxTargets
    const checkedCount = checkboxes.filter((cb) => cb.checked).length
    this.selectAllTarget.checked = checkedCount === checkboxes.length && checkboxes.length > 0
    this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length
  }

  updatePrintButton() {
    if (!this.hasPrintButtonTarget || !this.hasPrintButtonLabelTarget || !this.hasPrintTooltipTarget) {
      this.element.dispatchEvent(new CustomEvent("print-selection:updated", { bubbles: true }))
      return
    }

    const selectedCount = this.rowCheckboxTargets.filter((cb) => cb.checked).length
    if (selectedCount === 0) {
      this.printButtonTarget.disabled = true
      this.printButtonTarget.classList.add("btn-disabled")
      this.printButtonLabelTarget.textContent = "Print"
      this.printTooltipTarget.dataset.tip = "Select records to print"
    } else {
      this.printButtonTarget.disabled = false
      this.printButtonTarget.classList.remove("btn-disabled")
      this.printButtonLabelTarget.textContent = `Print (${selectedCount})`
      this.printTooltipTarget.dataset.tip = "Print selected records"
    }
    this.element.dispatchEvent(new CustomEvent("print-selection:updated", { bubbles: true }))
  }

  openModal() {
    const selectedCount = this.rowCheckboxTargets.filter((cb) => cb.checked).length
    if (selectedCount === 0) return

    if (this.skipColumnSelectionValue || !this.hasModalTarget) {
      this.print()
      return
    }

    this.modalTarget.showModal()
  }

  closeModal() {
    this.modalTarget.close()
  }

  reset() {
    this.rowCheckboxTargets.forEach((cb) => { cb.checked = false })
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.lastClickedRowIndex = null
    this.updatePrintButton()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  print() {
    const selectedIds = this.getSelectedIds()
    if (selectedIds.length === 0) {
      alert("Please select at least one record to print.")
      return
    }

    if (this.skipColumnSelectionValue) {
      this.submitPdfForm(selectedIds, [])
      return
    }

    const selectedColumnKeys = this.getSelectedColumnKeys()
    if (selectedColumnKeys.length === 0) {
      alert("Please select at least one column to print.")
      return
    }

    this.submitPdfForm(selectedIds, selectedColumnKeys)
  }

  getSelectedColumnKeys() {
    return this.columnCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.dataset.columnKey)
  }

  getSelectedIds() {
    return this.rowCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.value)
  }

  submitPdfForm(ids, columns) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.pdfUrlValue || "/inventories/pdf"
    form.target = "_blank"
    form.style.display = "none"

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "authenticity_token"
      input.value = token
      form.appendChild(input)
    }

    ids.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "ids[]"
      input.value = id
      form.appendChild(input)
    })

    columns.forEach((col) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "columns[]"
      input.value = col
      form.appendChild(input)
    })

    const formatInput = document.createElement("input")
    formatInput.type = "hidden"
    formatInput.name = "format"
    formatInput.value = "pdf"
    form.appendChild(formatInput)

    if (this.hasPrintNotesTarget) {
      const notesValue = this.printNotesTarget.value?.trim()
      if (notesValue) {
        const notesInput = document.createElement("input")
        notesInput.type = "hidden"
        notesInput.name = "notes"
        notesInput.value = notesValue
        form.appendChild(notesInput)
      }
    }

    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)

    this.closeModal()
  }
}
